/**
 * UpNextRail — right-side autoplay column shown next to the video player
 * on /watch/:id. Pulls from `related` (already loaded in Watch.jsx) and
 * also tags the **next** item with an "UP NEXT" pill so creators / viewers
 * know what plays after this one.
 *
 * On desktop it stacks vertically; on tablet it collapses below the
 * player; on mobile it's hidden (the existing "More in <category>" grid
 * lower down handles that).
 */
import React from "react";
import { Link } from "react-router-dom";
import { Play, Clock } from "lucide-react";

export default function UpNextRail({ items = [], currentId }) {
  // Drop the currently-playing item and dedupe.
  const list = items.filter((v) => v?.id && v.id !== currentId).slice(0, 12);
  if (list.length === 0) return null;
  return (
    <aside data-testid="up-next-rail" className="hidden lg:block w-[320px] shrink-0 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
      <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-black mb-3 flex items-center gap-1.5">
        <Play className="w-3 h-3" /> Up next
      </div>
      <ul className="space-y-2">
        {list.map((v, idx) => (
          <li key={v.id}>
            <Link
              to={`/watch/${v.id}`}
              data-testid={`up-next-${v.id}`}
              className="flex gap-3 group rounded-lg p-1.5 hover:bg-white/5 transition"
            >
              <div className="relative w-32 aspect-video rounded-md overflow-hidden bg-black shrink-0">
                {v.thumbnail_url
                  ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />
                  : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700" />}
                {idx === 0 && (
                  <span className="absolute top-1 left-1 bg-tv-accent text-black text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded">Up next</span>
                )}
                {v.duration_sec ? (
                  <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[9px] font-bold px-1 py-0.5 rounded inline-flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> {Math.floor(v.duration_sec / 60)}:{String(v.duration_sec % 60).padStart(2, "0")}
                  </span>
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold text-sm leading-tight line-clamp-2 group-hover:text-tv-accent transition">{v.title}</div>
                <div className="text-white/50 text-[11px] truncate mt-1">{v.creator_name}</div>
                {v.view_count != null && (
                  <div className="text-white/35 text-[10px] mt-0.5">{Number(v.view_count).toLocaleString()} views</div>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}
